import axios from "axios";
import type {
  AuthResponse,
  RegisterResponse,
  UserInfo,
  UserInfoResponse,
  Message,
  Playlist,
  Friendship,
} from "./interfaces";
import firebase from "firebase/app";
import "firebase/storage";
import "firebase/auth";
import { Notyf } from "notyf";
import store from "@/store/index";
import type { NavigationGuard } from "vue-router";

// Initialize Firebase
firebase.initializeApp({
  apiKey: "AIzaSyBD4TjGeZXKw7fWYR8X0UGfHAIvQqzUmF0",
  authDomain: "myapp-4f894.firebaseapp.com",
  databaseURL: "https://myapp-4f894.firebaseio.com",
  projectId: "myapp-4f894",
  storageBucket: "myapp-4f894.appspot.com",
  messagingSenderId: "410181839308",
  appId: "1:410181839308:web:7249de84cc8fdd3cc5d569",
});

// export const baseURI =
//   process.env.NODE_ENV === "production"
//     ? "https://web-chat-backend.onrender.com"
//     : "http://localhost:3003";
export const baseURI = "https://web-chat-backend.onrender.com";
export const defaultPageLimit = 50;

const pubKey =
  "BGtw8YFtyrySJpt8TrAIwqU5tlBlmcsdEinKxRKUDdb6fgQAnjVsS9N-ZhpAQzbwf78TMysYrMcuOY6T4BGJlwo";

export const CONSTANTS = Object.freeze({
  sideList: {
    MODES: {
      FRIENDS: 1,
      SEARCH: 2,
      FRIEND_REQUESTS: 3,
    },
  },
});
export const clearNotifications = async (searchOptions?: { tag: string }) => {
  if ("serviceWorker" in navigator) {
    return navigator.serviceWorker
      .getRegistration("/worker.js")
      .then((registration) => {
        if (registration) {
          registration.getNotifications(searchOptions).then((notifications) => {
            // since we're searching by tag it should only be one right?
            notifications.forEach((notification) => {
              notification.close();
            });
          });
        }
      });
  }
};

function createHeaders(headerObj: Record<string, any>) {
  const headers = new Headers();
  for (const headerName in headerObj) {
    if (Object.prototype.hasOwnProperty.call(headerObj, headerName)) {
      headers.set(headerName, String(headerObj[headerName]));
    }
  }
  return headers;
}

export const subscribeToNotif = async () => {
  const notification = new Notyf({
    duration: 7000,
    dismissible: true,
    position: { x: "center", y: "top" },
  });
  try {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.register(
        "/worker.js",
        {
          scope: "/",
        }
      );
      await registration.update();
      await requestPermission();
      const subscription =
        (await registration.pushManager.getSubscription()) ||
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(pubKey),
        }));
      await fetch(`${baseURI}/api/users/${store.state.user?.id}/subscribe`, {
        method: "POST",
        body: JSON.stringify(subscription),
        headers: createHeaders({
          "content-type": "application/json",
          "x-auth": getCookie("token"),
        }),
      });
    }
    store.commit("enablePush");
    notification.success(`successfully subscribed to push notifications`);
  } catch (error) {
    console.log(error);
    let parsedError = error;
    if (error instanceof Error) {
      parsedError = {
        errString: error.toString(),
        stack: error.stack,
        message: error.message,
      };
    }

    notification.error(
      `there was an error subscribing to the push notifications `
    );
    fetch(`${baseURI}/api/crashreport`, {
      method: "POST",
      body: JSON.stringify({
        error: parsedError,
        userId: store.state.user?.id,
        userAgent: navigator.userAgent,
      }),
      headers: createHeaders({
        "content-type": "application/json",
        "x-auth": getCookie("token"),
      }),
    });
  }
};

export const errorReport = (error: any, data?: Record<any, any>) => {
  const stack = error instanceof Error ? error.stack : new Error().stack;
  fetch(`${baseURI}/api/crashreport`, {
    method: "POST",
    body: JSON.stringify({
      error: error,
      userId: store.state.user?.id,
      userAgent: navigator.userAgent,
      data: { stack, data },
    }),
    headers: createHeaders({
      "content-type": "application/json",
      "x-auth": getCookie("token"),
    }),
  });
};

export const checkForDups = (messages: Message[], message: Message) => {
  if (messages.find(({ msgId }) => message.msgId === msgId)) {
    console.error("received message that was previously already sent", message);
    const stack = new Error().stack;
    fetch(`${baseURI}/api/crashreport`, {
      method: "POST",
      body: JSON.stringify({
        error: "received message that was previously already sent",
        userId: store.state.user?.id,
        userAgent: navigator.userAgent,
        data: { stack, message },
      }),
      headers: createHeaders({
        "content-type": "application/json",
        "x-auth": getCookie("token"),
      }),
    });
  }
};

export const unsubscribeToNotif = async () => {
  if (store.state.user) {
    await fetch(`${baseURI}/api/users/${store.state.user.id}/unsubscribe`, {
      method: "POST",
      headers: createHeaders({
        "content-type": "application/json",
        "x-auth": getCookie("token"),
      }),
    });
  }
};

export const updateDOMMessageStatus = (msgId: string, read: boolean) => {
  const messageElement = document.getElementById(msgId);
  if (messageElement) {
    messageElement.classList.remove("pending");
    messageElement.classList.remove("sent");
    messageElement.classList.remove("received");
    messageElement.classList.add(read ? "read" : "received");
  }
};

// from squoosh file sharing
export function getSharedImage(): Promise<File> {
  return new Promise((resolve) => {
    const onmessage = (event: MessageEvent) => {
      if (event.data.action !== "load-file") return;
      resolve(event.data.file);
      navigator.serviceWorker.removeEventListener("message", onmessage);
    };

    navigator.serviceWorker.addEventListener("message", onmessage);

    // This message is picked up by the service worker - it's how it knows we're ready to receive
    // the file.
    navigator.serviceWorker.controller?.postMessage("share-ready");
  });
}

export const uploadToFireBase = (file: File, location?: string) => {
  // Create a root reference
  const storageRef = firebase.storage().ref();
  const extension = file.name.split(".").pop();
  const ref = storageRef.child(
    /** @nb the off chance a users uploads 2 things at the same time? */
    `${location || ""}/${Date.now()}.${extension}`
  );
  return ref.put(file).then(function (snapshot) {
    return snapshot.ref.getDownloadURL();
  });
};

export const addAudioToFirebase = (blob: Blob, friendship_id: string) => {
  const storageRef = firebase.storage().ref();
  const ref = storageRef.child(
    `voicenotes/${
      store.state.user?.firebaseUid || store.state.user?.id
    }/${friendship_id}/${Date.now()}`
  );
  return ref.put(blob, { contentType: "audio/webm" }).then(function (snapshot) {
    return snapshot.ref.getDownloadURL();
  });
};

export const signInToFirebase = (token: string) => {
  return firebase
    .auth()
    .signInWithCustomToken(token)
    .catch(function (error) {
      // Handle Errors here.
      const errorCode = error.code;
      const errorMessage = error.message;
      console.log(errorCode, errorMessage);
      // ...
    });
};

export const getFirebaseSigninToken = () => {
  return fetch(`${baseURI}/api/users/me/generatefbtoken`, {
    method: "POST",
    headers: createHeaders({
      "x-auth": getCookie("token"),
    }),
  }).then((res) => res.json());
};

export const signOutOfFirebase = () => {
  return firebase
    .auth()
    .signOut()
    .catch(function (error) {
      // Handle Errors here.
      const errorCode = error.code;
      const errorMessage = error.message;
      console.log(errorCode, errorMessage);
      // ...
    });
};

export function encodeBase64(file: File) {
  return new Promise((resolve, reject) => {
    const imgReader = new FileReader();
    imgReader.onloadend = function () {
      resolve(imgReader.result);
    };
    imgReader.onerror = reject;
    imgReader.readAsDataURL(file);
  });
}

export const login = async (userData: {
  username: string;
  password: string;
}): Promise<AuthResponse> => {
  const { data: authData } = await axios({
    method: "POST",
    url: `${baseURI}/api/login`,
    data: userData,
  });
  setCookie("username", authData.username, 1000000);
  setCookie("token", authData.token, 1000000);
  /** i dont think we necessarily need to wait on or keep track of this
   * it should complete before the user tries to send any images or audio,
   * remember this is required for only writes and not reads */
  getFirebaseSigninToken().then(({ token }) => signInToFirebase(token));
  await store.dispatch("setUpApp");
  return authData;
};

export const loginWithGoogle = async (): Promise<AuthResponse> => {
  await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
  const provider = new firebase.auth.GoogleAuthProvider();
  return firebase
    .auth()
    .signInWithPopup(provider)
    .then(async (data) => {
      const userData = {
        username:
          data.user?.displayName ||
          data.additionalUserInfo?.username ||
          (data.additionalUserInfo?.profile as Record<string, string>)
            ?.given_name,
        email: data.user?.email,
        firebaseUid: data.user?.uid,
        firebaseCreated: true,
      };
      // if the account already exists then we kinda do this for nothing
      const unique = await checkusername(userData.username);
      if (!unique) userData.username += Math.floor(Math.random() * 1000);
      // let authData = (await signup(userData)).data;
      const { data: authData } = await axios({
        method: "POST",
        url: `${baseURI}/api/loginWithCustomProvider`,
        data: userData,
      });
      // .catch(error => {
      //   if (error.response && error.response.status == 409) {
      //     if (error.response.data.fields.includes("username")) {
      //       return {
      //         newUsername: true
      //       };
      //     }
      //   } else throw error;
      // });
      setCookie("username", authData.username, 1000000);
      setCookie("token", authData.token, 1000000);
      /** i dont think we necessarily need to wait on or keep track of this
       * it should complete before the user tries to send any images or audio,
       * remember this is required for only writes and not reads */
      await store.dispatch("setUpApp");
      return authData;
    });
};

export const logout = async () => {
  try {
    await Promise.all([
      /** @todo send some indication this was an automatic unsubcription, maybe in
       * the future we can treat this user differently
       */
      unsubscribeToNotif(),
      signOutOfFirebase(),
      store.state.db.clear("users"),
      store.state.db.clear("messages"),
      store.state.db.clear("friendShips"),
      // deleteDB("app")
    ]);
    axios({
      method: "POST",
      headers: {
        "x-auth": getCookie("token"),
      },
      url: `${baseURI}/api/logout`,
    }).catch(console.log);
    await store.commit("resetState");
    setCookie("token", "", -1000);
    setCookie("username", "", -1000);
    setCookie("io", "", -1000);
  } catch (error: any) {
    console.log(error);
  }
};

export const signup = async (userData: {
  username: string;
  email?: string;
  password: string;
  confirmPassword: string;
}): Promise<RegisterResponse> => {
  return await axios({
    method: "POST",
    url: `${baseURI}/api/signup`,
    data: userData,
  });
};

export const sortMessageArray = (a: Message, b: Message) => {
  if (a.createdAt > b.createdAt) {
    return 1;
  } else if (a.createdAt < b.createdAt) {
    return -1;
  } else {
    return 0;
  }
};

export const checkusername = async (username: string): Promise<boolean> => {
  return await axios({
    method: "GET",
    url: `${baseURI}/api/user_exists?username=${username}`,
  }).then((res) => {
    return !res.data.exists;
  });
};

export const getUserInfo = async (): Promise<{ data: UserInfo }> => {
  return await axios({
    method: "GET",
    headers: {
      "x-auth": getCookie("token"),
    },
    url: `${baseURI}/api/users/me`,
  });
};

export const getUsers = async (
  username: string
): Promise<{ data: UserInfo[] }> => {
  return await axios({
    method: "GET",
    headers: {
      "x-auth": getCookie("token"),
    },
    url: `${baseURI}/api/users?username=${username}&exclude_me`,
  });
};

export const getUserById = async (userId: string): Promise<UserInfo> => {
  return await axios({
    method: "GET",
    headers: {
      "x-auth": getCookie("token"),
    },
    url: `${baseURI}/api/users?user_id=${userId}&exclude_me`,
  }).then(({ data }) => data[0]);
};

export function getDimensionsForBox({
  containerWidth,
  containerHeight,
  objectWidth,
  objectHeight,
}: {
  containerWidth: number;
  containerHeight?: number;
  objectWidth: number;
  objectHeight: number;
}): Map<string, number> {
  const WHRatio = objectWidth / objectHeight;
  const propertyMap = new Map();
  /** if it id wider than it is tall */
  if (!containerHeight) {
    if (WHRatio >= 1) {
      propertyMap.set("width", containerWidth);
      propertyMap.set("height", containerWidth / WHRatio);
      /** @todo expansion logic */
    } else if (WHRatio < 1) {
      propertyMap.set("height", containerWidth);
      propertyMap.set("width", containerWidth * WHRatio);
    }
    return propertyMap;
  }
  /**
   * if the resultant height for the containerWidth we would like to use is within the constrainst
   */
  if (containerWidth / WHRatio < containerHeight) {
    propertyMap.set("width", containerWidth);
    propertyMap.set("height", containerWidth / WHRatio);
    return propertyMap;
  } else {
    /** once we get here then the widrh is guaranteed to be less than the constraint */
    propertyMap.set("height", containerHeight);
    propertyMap.set("width", containerHeight * WHRatio);
    return propertyMap;
  }
}

export const updateInfo = async (
  userData: UserInfo
): Promise<UserInfoResponse> => {
  return await axios({
    method: "POST",
    data: userData,
    headers: {
      "x-auth": getCookie("token"),
    },
    url: `${baseURI}/api/users/me`,
  });
};

// adapted from https://bobbyhadz.com/blog/javascript-convert-timestamp-to-time-ago
export function relativeDays(timestamp: number) {
  if (!timestamp) {
    return "n/a";
  }

  const rtf = new Intl.RelativeTimeFormat("en", {
    numeric: "auto",
  });
  const oneDayInMs = 1000 * 60 * 60 * 24;
  const oneHourInMs = 1000 * 60 * 60;
  const oneMinuteInMs = 1000 * 60;
  const oneSecondInMs = 1000;
  const daysDifference = Math.round(
    (timestamp - new Date().getTime()) / oneDayInMs
  );
  const hoursDifference = Math.round(
    (timestamp - new Date().getTime()) / oneHourInMs
  );
  const minutesDifference = Math.round(
    (timestamp - new Date().getTime()) / oneMinuteInMs
  );
  const secondsDifference = Math.round(
    (timestamp - new Date().getTime()) / oneSecondInMs
  );
  if (daysDifference < 0) {
    return rtf.format(daysDifference, "day");
  } else if (hoursDifference < 0) {
    return rtf.format(hoursDifference, "hour");
  } else if (minutesDifference < 0) {
    return rtf.format(minutesDifference, "minute");
  } else if (secondsDifference < 0) {
    return rtf.format(secondsDifference, "second");
  }
}

export function getCookie(name: string): string | null {
  const exp = new RegExp(`(?:(?:^|.*;\\s*)${name}\\s*\\=\\s*([^;]*).*$)|^.*$`);
  const cookieValue = document.cookie.replace(exp, "$1");
  return cookieValue !== "" ? cookieValue : null;
}

export const getFriendShips = async () => {
  return await axios({
    method: "GET",
    headers: {
      "x-auth": getCookie("token"),
    },
    url: `${baseURI}/api/users/me/friends`,
  }).then(({ data }) => {
    return data;
  });
};

export const addFriend = async ({
  username,
  id,
}: {
  username: string;
  id: string;
}) => {
  return await axios({
    method: "POST",
    headers: {
      "x-auth": getCookie("token"),
    },
    // endpoint will prioritize id
    data: { username, id },
    url: `${baseURI}/api/users/me/friends`,
  });
};
export const sendRequest = async (friendId: string) => {
  return await axios({
    method: "POST",
    headers: {
      "x-auth": getCookie("token"),
    },
    data: { friendId },
    url: `${baseURI}/api/users/me/friendRequests`,
  }).then(({ data }) => {
    store.commit("updateInteractions", data.interactions);
    return data;
  });
};

export const setCookie = (name: string, value: string, days: number) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
};

/**
 * possibly do this on the socket intead
 * currently doing it this way because the socket is a property of the homme component
 * and i also want to call this function immediately inside of get messages (or any function that gets messages)
 * we will not have reference to the inside those functions and i dont know if i want to pass it to them
 * */
export const markAsReceived = async (
  friendship_id: string,
  range: number[]
) => {
  return await axios({
    method: "POST",
    url: `${baseURI}/io/users/me/${friendship_id}/sweep`,
    headers: {
      "Content-type": "application/json",
      "x-auth": getCookie("token"),
    },
    data: {
      range,
      friendship_id,
      read: store.getters.isInChat == friendship_id,
    },
  });
};

export const getYouTubeVideoID = (link: string) => {
  let vidId;
  try {
    const url = new URL(link);
    if (url.hostname === "www.youtube.com") {
      vidId = url.searchParams.get("v");
    } else if (url.hostname === "youtu.be") {
      vidId = url.pathname.substr(1);
    } else {
      return;
    }
  } catch (e) {
    console.log(e);
  }
  return vidId;
};

export const checkAndLoadAppUpdate = () => {
  fetch("/versionuid.json").then(async (resp) => {
    const respJSON = await resp.json();
    console.log(respJSON.uuid, __VITE_VUE_APP_VER__);

    if (respJSON.uuid !== __VITE_VUE_APP_VER__) {
      // we need to update app (cache)
      caches.delete("v2").then(async () => {
        store.state.messageNotification.success(
          "New app version available refresh to see"
        );
        // TODO: for now we wont do this because it causes sync issues when doing multiple updates
        // we need to get list of assets here
        // let v2Cache = await caches.open("v2");
        // v2Cache.addAll(["/home", "/profile"]);
      });
    }
  });
};

export const countUnreads = ({
  chat,
  user_id,
}: {
  chat: Message[];
  user_id: string;
}) => {
  const unreadLimit = 50;
  let unreads = 0;
  if (chat) {
    const arrLength = chat.length;
    const messages = chat.slice(arrLength - (unreadLimit + 1));
    for (const message of messages) {
      if (message.status !== "read" && message.fromId !== user_id) {
        unreads++;
      }
    }
  }

  return unreads;
};

export const isInChat = (friendship_id: string) => {
  return store.getters.isInChat === friendship_id;
};

export function markLocalChatMessagesAsRead(
  messages: Message[],
  userId: string
) {
  /**
   * this is all used in order to keep count of the unread badge that will be on the chat
   * and is not the same as us getting info that the other user has read our messages
   */
  const unreadLimit = 50;
  const start = Math.max(messages.length - unreadLimit, 0);
  for (let i = start; i < messages.length; i++) {
    if (userId !== messages[i].fromId) {
      messages[i].status = "read";
    }
  }
  return messages;
}

export const getNotifications = async () => {
  return await axios({
    method: "GET",
    url: `${baseURI}/api/users/me/notifications`,
    headers: {
      "Content-type": "application/json",
      "x-auth": getCookie("token"),
    },
  });
};

export const getPlaylists = async (): Promise<Playlist[]> => {
  // TODO: FIXME: Use the store as a cache instead of doing this and skipping out the store
  return store.dispatch("emitEvent", {
    eventName: "getPlaylists",
  });
};

export const getMessages = async (friendship_id: string, limit = 50) => {
  return await axios({
    method: "GET",
    url: `${baseURI}/api/users/me/${friendship_id}/messages?limit=${limit}`,
    headers: {
      "Content-type": "application/json",
      "x-auth": getCookie("token"),
    },
  }).then(({ data: messages }) => {
    /** lower limit is earliest message */
    if (messages.length > 0) {
      markAsReceived(friendship_id, [
        messages[0].createdAt,
        messages[messages.length - 1].createdAt,
      ]).catch(() => {
        console.log("failed to mark as received");
      });
    }
    return { data: messages };
  });
};

export const getMessagePage = async ({
  friendship_id,
  limit = defaultPageLimit,
  timestamp,
  msgId,
}: {
  friendship_id: string;
  limit: number;
  timestamp: number;
  msgId: string;
}) => {
  return await axios({
    method: "GET",
    url: `${baseURI}/api/users/me/${friendship_id}/messagespage?limit=${limit}&timestamp=${timestamp}&msgId=${msgId}`,
    headers: {
      "Content-type": "application/json",
      "x-auth": getCookie("token"),
    },
  }).then(({ data: messages }) => {
    /** remember in data the last message is the first item in the array */
    markAsReceived(friendship_id, [
      messages[0].createdAt,
      messages[messages.length - 1].createdAt,
    ]).catch(() => {
      console.log("failed to mark as received");
    });
    return { data: messages };
  });
};

export async function getPreviewData(url: string) {
  const id = uuid();
  return fetch(`${baseURI}/api/getpreview`, {
    method: "POST",
    body: JSON.stringify({ url, id }),
    headers: createHeaders({
      "content-type": "application/json",
      "x-auth": getCookie("token"),
    }),
  }).then((res) => res.json());
}

export function uuid() {
  let date = new Date().getTime();
  const randomStrings = (c: string) => {
    const r = (date + Math.random() * 16) % 16 | 0;
    date = Math.floor(date / 16);
    return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
  };
  const id = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    randomStrings
  );
  return id;
}

export const sortByCreatedAt = (
  friendshipA: Friendship,
  friendshipB: Friendship
) => {
  const lastMessageA = friendshipA.lastMessage[0];
  const lastMessageB = friendshipB.lastMessage[0];

  if (!lastMessageB || !("createdAt" in lastMessageB)) {
    return -1;
  }
  if (!lastMessageA || !("createdAt" in lastMessageA)) {
    return 1;
  }
  return (lastMessageB.createdAt || 0) - (lastMessageA.createdAt || 0);
};

export const binaryCustomSearch = function (
  arr: { createdAt: number }[],
  x: { createdAt: number }
) {
  if (!arr || arr.length === 0) {
    return -1;
  }

  let start = 0,
    end = arr.length - 1,
    gt = -1,
    lt = -1;

  // Iterate while start not meets end
  while (start <= end) {
    // Find the mid index
    const mid = Math.floor((start + end) / 2);

    // If element is present at mid, return True
    if (arr[mid].createdAt === x.createdAt) {
      return mid;
    }

    // Else look in left or right half accordingly
    else if (arr[mid].createdAt < x.createdAt) {
      start = mid + 1;
      gt = mid;
    } else {
      end = mid - 1;
      lt = mid;
    }
  }

  return { lt, gt };
};

export const getLastMessage = async (friendship_id: string) => {
  return await axios({
    method: "GET",
    url: `${baseURI}/api/users/me/${friendship_id}/lastmessage`,
    headers: {
      "Content-type": "application/json",
      "x-auth": getCookie("token"),
    },
  });
};

export const eventWrapper = (
  eventName: string,
  handler?: (data: Record<string, any>) => void
) => {
  return (data: Record<string, any>) => {
    if (data && data.eventData) {
      store.commit("addEvent", data.eventData);
    }
    // FIXME:  doing this here defeats the entire purpose of having one time listeners
    const OThandlers = store.state.oneTimeListeners.get(eventName);
    if (OThandlers) {
      for (const OThandler of OThandlers.values()) {
        OThandler(data);
      }
    }
    if (handler) handler(data);
    return;
  };
};

export const authBeforeEnter: NavigationGuard = (to, from, next) => {
  const token = getCookie("token");
  if (!token) {
    next("/login");
    return;
  }
  next();
};

export const upgradeToAuth: NavigationGuard = (to, from, next) => {
  const token = getCookie("token");
  if (token) {
    next("/");
    return;
  }
  next();
};

/**
 * @todo move to state
 */
export function enableSound() {
  setCookie("soundNotifPref", "true", 1000000);
  store.state.enableSoundNotif = true;
}
export function disableSound() {
  setCookie("soundNotifPref", "false", 1000000);
  store.state.enableSoundNotif = false;
}

export const notifyMe = (data: Partial<Message>) => {
  try {
    const { friendship_id, msgId, from, text } = data;
    if (!("Notification" in window)) {
      return;
    } else if (Notification.permission === "granted") {
      navigator.serviceWorker.controller?.postMessage({
        action: "notify",
        data: {
          friendship_id,
          _id: msgId,
          text,
          title: from,
        },
      });
    }

    // At last, if the user has denied notifications, and you
    // want to be respectful there is no need to bother them any more.
  } catch (error) {
    console.log(error);
  }
};

async function requestPermission() {
  if (Notification.permission === "granted") {
    return true;
  }

  // Otherwise, we need to ask the user for permission
  else if (Notification.permission !== "denied") {
    return Notification.requestPermission().then(function (permission) {
      // If the user accepts, let's create a notification
      if (permission === "granted") {
        return true;
      }
      return false;
    });
  } else {
    return false;
  }
}

/** @todo eventually refactor and let this replace the old one */
export const scrollBottom2 = function scrollBottom({
  element,
  force,
  test,
  ...optionals
}: {
  element: Element;
  force: boolean;
  test: boolean;
  noAnim?: boolean;
}) {
  const newMessage = element.querySelector(
    ".date__group:last-of-type li:last-of-type"
  );
  if (!newMessage) return;
  let doScroll = !!force;
  if (newMessage) {
    const clientHeight = element.clientHeight;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;

    const newMessageHeight = newMessage.clientHeight;
    const lastMessage = element.querySelector(
      ".date__group:last-of-type li:nth-last-of-type(2)"
    );

    const lastMessageHeight = lastMessage ? lastMessage.clientHeight : 0;
    doScroll = !doScroll
      ? clientHeight + scrollTop + newMessageHeight + lastMessageHeight >=
        scrollHeight
      : doScroll;
    if (doScroll && !test) {
      element.scrollTo({
        top: scrollHeight + newMessageHeight + newMessageHeight,
        behavior: optionals.noAnim ? "auto" : "smooth",
      });

      return scrollHeight + newMessageHeight + newMessageHeight;
    } else if (test) {
      return doScroll;
    }
  }
};

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function dragElement(elmnt: HTMLElement) {
  let pos1 = 0,
    pos2 = 0,
    pos3 = 0,
    pos4 = 0;
  const header = document.getElementById(elmnt.id + "header");
  if (header) {
    // if present, the header is where you move the DIV from:
    header.onmousedown = dragMouseDown;
    elmnt.addEventListener("touchstart", dragMouseDown, { passive: false });
  } else {
    // otherwise, move the DIV from anywhere inside the DIV:
    elmnt.onmousedown = dragMouseDown;
    elmnt.addEventListener("touchstart", dragMouseDown, { passive: false });
  }

  function dragMouseDown(e: MouseEvent | TouchEvent) {
    e = e || window.event;
    // get the mouse cursor position at startup:
    pos3 = e.clientX;
    pos4 = e.clientY;
    document.body.classList.add("no-overscroll");
    document.onmouseup = closeDragElement;
    document.addEventListener("touchcancel", closeDragElement, {
      passive: false,
    });
    document.addEventListener("touchend", closeDragElement, { passive: false });
    // call a function whenever the cursor moves:
    document.onmousemove = elementDrag;
    document.addEventListener("touchmove", elementDrag, { passive: false });
  }

  function elementDrag(e: MouseEvent | Touch) {
    e = e.changedTouches?.[0] || e || window.event;
    e.preventDefault?.();
    // calculate the new cursor position:
    pos1 = pos3 - e.clientX;
    pos2 = pos4 - e.clientY;
    pos3 = e.clientX;
    pos4 = e.clientY;
    // set the element's new position:
    elmnt.style.top = elmnt.offsetTop - pos2 + "px";
    elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
  }

  function closeDragElement() {
    // stop moving when mouse button is released:
    document.body.classList.remove("no-overscroll");
    document.onmouseup = null;
    document.onmousemove = null;
    document.removeEventListener("touchend", closeDragElement);
    document.removeEventListener("touchcancel", closeDragElement);
    document.removeEventListener("touchmove", elementDrag);
  }
}
